import { useState, useCallback, useRef, useEffect } from 'react';
import { API_BASE_URL } from '../config/apiConfig';
import { SessionManager } from '../redux/sessionManager';
import type { AgentMessage, AgentMessagePart, AgentChatEvent } from '../types/agent';

export function useAgentChat(siteId: string | null) {
  const [messages, setMessages] = useState<AgentMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const prevSiteIdRef = useRef(siteId);

  useEffect(() => {
    if (prevSiteIdRef.current !== siteId) {
      prevSiteIdRef.current = siteId;
      abortRef.current?.abort();
      setMessages([]);
      setSessionId(null);
      setIsStreaming(false);
    }
  }, [siteId]);

  const sendMessage = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;

      // Add user message
      const userMsg: AgentMessage = {
        id: Date.now().toString(),
        role: 'user',
        content: trimmed,
        timestamp: new Date(),
      };

      // Create assistant message shell
      const assistantId = (Date.now() + 1).toString();
      const assistantMsg: AgentMessage = {
        id: assistantId,
        role: 'assistant',
        parts: [],
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      setIsStreaming(true);

      const controller = new AbortController();
      abortRef.current = controller;

      try {
        const authHeaders = SessionManager.getAuthHeaders();
        const response = await fetch(`${API_BASE_URL}/api/v1/agent/chat`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'text/event-stream',
            ...authHeaders,
          },
          body: JSON.stringify({
            message: trimmed,
            site_id: siteId,
            session_id: sessionId,
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            const data = line.slice(5).trim();
            if (!data) continue;

            let event: AgentChatEvent;
            try {
              event = JSON.parse(data);
            } catch {
              continue;
            }

            if (event.type === 'done') {
              if (event.session_id) {
                setSessionId(event.session_id);
              }
              setIsStreaming(false);
              // Finalize any open thinking parts
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== assistantId) return m;
                  const parts = (m.parts || []).map((p) => {
                    if (p.type === 'thinking' && !p.completedAt) {
                      return { ...p, completedAt: Date.now() };
                    }
                    return p;
                  });
                  return { ...m, parts };
                })
              );
              continue;
            }

            if (event.type === 'error') {
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== assistantId) return m;
                  const parts: AgentMessagePart[] = [
                    ...(m.parts || []),
                    { type: 'text', text: event.text || 'An error occurred.' },
                  ];
                  return { ...m, parts };
                })
              );
              setIsStreaming(false);
              continue;
            }

            // Update the assistant message parts based on event type
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== assistantId) return m;
                const parts = [...(m.parts || [])];

                switch (event.type) {
                  case 'thinking': {
                    // Close previous thinking if exists, or append to current
                    const lastThinking = parts.findIndex(
                      (p) => p.type === 'thinking' && !p.completedAt
                    );
                    if (lastThinking >= 0) {
                      const existing = parts[lastThinking] as Extract<
                        AgentMessagePart,
                        { type: 'thinking' }
                      >;
                      parts[lastThinking] = {
                        ...existing,
                        text: existing.text + (event.text || ''),
                      };
                    } else {
                      parts.push({
                        type: 'thinking',
                        text: event.text || '',
                        startedAt: Date.now(),
                      });
                    }
                    break;
                  }

                  case 'tool_call': {
                    // Close any open thinking
                    for (let i = 0; i < parts.length; i++) {
                      const p = parts[i];
                      if (p.type === 'thinking' && !p.completedAt) {
                        parts[i] = { ...p, completedAt: Date.now() };
                      }
                    }
                    parts.push({
                      type: 'tool_call',
                      toolName: event.tool_name || 'unknown',
                      args: (event.tool_args as Record<string, unknown>) || {},
                      status: 'pending',
                      eventId: event.event_id || Date.now().toString(),
                    });
                    break;
                  }

                  case 'tool_result': {
                    // Find the last pending tool_call with matching name
                    for (let i = parts.length - 1; i >= 0; i--) {
                      const p = parts[i];
                      if (
                        p.type === 'tool_call' &&
                        p.status === 'pending' &&
                        p.toolName === event.tool_name
                      ) {
                        const hasError =
                          event.result &&
                          typeof event.result === 'object' &&
                          'error' in event.result;
                        parts[i] = {
                          ...p,
                          status: hasError ? 'error' : 'complete',
                          result: event.result,
                        };
                        break;
                      }
                    }
                    break;
                  }

                  case 'text': {
                    // Close any open thinking
                    for (let i = 0; i < parts.length; i++) {
                      const p = parts[i];
                      if (p.type === 'thinking' && !p.completedAt) {
                        parts[i] = { ...p, completedAt: Date.now() };
                      }
                    }
                    // Append to last text part or create new one
                    const lastText = parts.length > 0 ? parts[parts.length - 1] : null;
                    if (lastText && lastText.type === 'text') {
                      parts[parts.length - 1] = {
                        ...lastText,
                        text: lastText.text + event.text,
                      };
                    } else {
                      parts.push({ type: 'text', text: event.text || '' });
                    }
                    break;
                  }
                }

                return { ...m, parts };
              })
            );
          }
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          // User cancelled
        } else {
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== assistantId) return m;
              const parts: AgentMessagePart[] = [
                ...(m.parts || []),
                {
                  type: 'text',
                  text: 'Failed to connect to the AI assistant. Please try again.',
                },
              ];
              return { ...m, parts };
            })
          );
        }
      } finally {
        setIsStreaming(false);
        abortRef.current = null;
      }
    },
    [siteId, sessionId, isStreaming]
  );

  const stopStreaming = useCallback(() => {
    abortRef.current?.abort();
    setIsStreaming(false);
  }, []);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setSessionId(null);
  }, []);

  return { messages, sendMessage, isStreaming, stopStreaming, clearMessages };
}
